import { useEffect, useState } from "react"
import TagManager from "react-gtm-module"
import { QueryClient, QueryClientProvider } from "react-query"
// import { LandingPage } from "@fuse/pages/landingPage";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import { StripeProvider } from "@fuse/customProviders/StripeProvider"
import { EFeatureFlags, TWebUser } from "@letshang/constants"
import { loadConnectAndInitialize } from "@stripe/connect-js"
import { ConnectComponentsProvider } from "@stripe/react-connect-js"
import { clarity } from "clarity-js"
import { signOut, User } from "firebase/auth"
import { size } from "lodash-es"
import { useRecoilState, useRecoilValue } from "recoil"
import withProviders from "src/withProviders"

import ProtectedRouteWrapper from "@navigation/ProtectedRouteWrapper"

import {
  defaultUserInfoWithBuddies,
  userInfoWithBuddiesState,
} from "@fuse/recoil/buddies"
import {
  requestedSuggestedBuddiesAtom,
  suggestedBuddiesAtom,
} from "@fuse/recoil/buddies"
import { defaultUser, userState } from "@fuse/recoil/signup"
import { stripeUrlState } from "@fuse/recoil/stripe"

import { auth } from "@firebaseConfig/clientApp"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"
import { DEV } from "@settingsAlias/settings"

import { getUserInfo, onboardUserToStripe } from "@api/firebaseWrappedApis"

import { isDev } from "@fuse/utils/DeviceDection"

import { useDevEmailVerification } from "@fuse/customHooks/useDevEmailVerification"
import useTrackUser from "@fuse/customHooks/useTrackUser"

import { DevEmailIndicator } from "@fuse/components/DevEmailIndicator"

// Import all your page components...
import { ClaimTicketLandingPage } from "@fuse/pages/claimTicket"
import CompleteProfile from "@fuse/pages/completeProfile"
import { VerifyEmail } from "@fuse/pages/emailVerification/verifyEmail"
import { EventLandingPage } from "@fuse/pages/eventPage"
import CreateEventPage from "@fuse/pages/eventPage/createEvent"
import CreateEventPageV2 from "@fuse/pages/eventPage/createEvent-v2"
import CreateEventPageV3 from "@fuse/pages/eventPage/createEvent-v3"
import CreateEventPageV4 from "@fuse/pages/eventPage/createEvent-v4"
import CreateEventPageV5 from "@fuse/pages/eventPage/createEvent-v5"
import EditEventV5 from "@fuse/pages/eventPage/createEvent-v5/EditEventV5"
import CreateEventPageV6 from "@fuse/pages/eventPage/createEvent-v6"
import EventCreateSuccess from "@fuse/pages/eventPage/createEvent/EventCreateSuccess"
import PaymentInsights from "@fuse/pages/eventPage/payment-insights"
import EventRoulette from "@fuse/pages/eventRoullete"
import EventSignUp from "@fuse/pages/eventSignUp"
import ChooseTicket from "@fuse/pages/eventSignUp/BuyTickets/ChooseTicket"
import FillFriendsTicketInfo from "@fuse/pages/eventSignUp/BuyTickets/FillFriendsTicketInfo"
import FillQuestionnaire from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaire"
import FillQuestionnaireUpdated from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaireUpdated"
import StripeCheckout from "@fuse/pages/eventSignUp/BuyTickets/StripeCheckout"
import StripeSuccess from "@fuse/pages/eventSignUp/BuyTickets/StripeSuccess"
import MyEventTicket from "@fuse/pages/eventSignUp/MyEventTicket"
import IndividualTicket from "@fuse/pages/eventSignUp/MyEventTicket/IndividualTicket"
import TicketCollection from "@fuse/pages/eventSignUp/MyEventTicket/TicketCollection"
import EventTemplates from "@fuse/pages/eventTemplates"
import Explore from "@fuse/pages/explore"
import { GroupLandingPage } from "@fuse/pages/groupPage"
import GuestList from "@fuse/pages/guestList"
import ReviewQuestionnaire from "@fuse/pages/guestList/ReviewQuestionnaire"
import LandingPage from "@fuse/pages/landingPage/index"
import NotImplementedPage from "@fuse/pages/NotImplementedPage"
import OrganizationProfile from "@fuse/pages/organizations"
import PeoplePage from "@fuse/pages/people"
import PluginDetailPage from "@fuse/pages/plugins/PluginDetailPage"
import PluginDevelopmentStudio from "@fuse/pages/plugins/views/PluginDevelopmentStudio"
import ProfilePage from "@fuse/pages/profilePage"
import SignUpPage from "@fuse/pages/signUp"
import SpecialEvent from "@fuse/pages/specialEvent"
import StripeConnectionStatus from "@fuse/pages/stripeConnectionStatus"

import "./App.css"

export const ROUTES = {
  LANDING: "/",
  EVENTS: "/events/:eventId",
  SHARE_EVENTS: "share/events/:eventId",
  GROUPS: "/groups/*",
  SIGNUP: "/signup",
  PROFILE: "/profile",
  EVENT_SIGNUP: "/eventSignup",
  CHOOSE_TICKET: "/events/:eventId/eventSignup/chooseTicket",
  FILL_FRIENDS_TICKET_INFO:
    "/events/:eventId/eventSignup/fillFriendsTicketInfo",
  FILL_QUESTIONNAIRE: "/events/:eventId/eventSignup/fillQuestionnaire",
  FILL_QUESTIONNAIRE_UPDATED:
    "/events/:eventId/eventSignup/fillQuestionnairev2",
  STRIPE_CHECKOUT: "/events/:eventId/eventSignup/stripeCheckout",
  STRIPE_SUCCESS: "/events/:eventId/eventSignup/stripeSuccess",
  MY_TICKET: "/events/:eventId/tickets/:attendeeId",
  VERIFY_EMAIL: "/verify/email",
  EVENT_ROULLETE: "/event-roulette",
  EXPLORE: "/explore",
  SPECIAL_EVENT: "/special-event",
  PEOPLE: "/people",
  ORGANIZATIONS: "/organizations/:orgId",
  SHARE_ORGANIZATIONS: "share/organizations/:orgId",
  CLAIM_TICKET: "/claim_ticket/:eventId/:phoneNumber",
  TICKETS: "/events/:eventId/tickets",
  MY_TICKET_ID: ":attendeeId",
  SEARCH: "/search",
  CREATE_EVENT: "/create-event",
  CREATE_EVENT_FAST_SIGN_UP: "/create-event-v2",
  CREATE_EVENT_V3: "/create-event-v3",
  CREATE_EVENT_V4: "/create-event-v4",
  CREATE_EVENT_V5: "/create-event-v5",
  CREATE_EVENT_V6: "/create-event-v6",
  EVENT_CREATE_SUCCESS: "/create-event/success",
  EVENT_TEMPLATES: "/event-templates",
  COMPLETE_PROFILE: "/complete-profile",
  PLUGIN_DETAILS: "/plugins/:pluginId",
  PLUGIN_STUDIO_CREATION: "/plugin-studio/create",
  EDIT_EVENT: "/edit-event/:eventId",
  STRIPE_ONBOARDING_SUCCESS: "/stripe-onboarding",
  GUEST_LIST: "/:eventId/guestList",
  REVIEW_QUESTIONNAIRE: "/:eventId/reviewQuestionnaire",
  PAYMENT_INSIGHTS: "/:eventId/paymentInsights",
}

const queryClient = new QueryClient()

const RedirectTo = () => {
  const location = useLocation()
  const newPath = location.pathname.replace("share/", "")
  return <Navigate to={`${newPath}`} replace />
}

const CheckForRedirection = ({ children }: { children: React.ReactNode }) => {
  const [redirectionalUrl, _] = useState(() =>
    localStorage.getItem("fastSignUpMainUrl"),
  )
  if (redirectionalUrl) {
    localStorage.removeItem("fastSignUpMainUrl")
    return <Navigate to={`${redirectionalUrl}`} replace />
  } else {
    return <>{children}</>
  }
}

// Dynamic Stripe Connect Provider Component
const DynamicStripeConnectProvider = ({
  children,
  user,
  stripeUrl,
}: {
  children: React.ReactNode
  user: any
  stripeUrl: any
}) => {
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const isStripeEmbeddedEnabled = useProcessFeatureFlags(
    EFeatureFlags.EMBEDDED_STRIPE_WEB,
  )

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        // Check if we have all required conditions
        const hasUser = user?.uid
        const hasStripeUrl = stripeUrl?.url && stripeUrl.url.trim() !== ""
        // Default to true if not specified

        console.log("Stripe Connect initialization check:", {
          hasUser,
          hasStripeUrl,
          isStripeEmbeddedEnabled,
          stripeUrl: stripeUrl?.url,
        })

        // Only initialize if we have user, valid stripe URL, and feature is enabled
        if (!hasUser || !hasStripeUrl || !isStripeEmbeddedEnabled) {
          console.log(
            "Skipping Stripe Connect initialization - requirements not met",
          )
          setStripeConnectInstance(null)
          setIsInitialized(true)
          return
        }

        console.log("Initializing Stripe Connect with URL:", stripeUrl.url)

        const fetchClientSecret = async () => {
          const clientSecret = stripeUrl.url
          if (!clientSecret || clientSecret.trim() === "") {
            throw new Error("No valid client secret available")
          }
          console.log("Returning client secret for Stripe Connect")
          return clientSecret
        }

        const instance = loadConnectAndInitialize({
          publishableKey:
            "pk_test_51PId5GEWqBMHeCvg0pHnvNinXv7w37PGnvB2G5zBmO8CAFLJHTbWdNur1yuJ6G1fz575FilBwz3mjs7JeDcoIJ5900bXkUNnR6",
          fetchClientSecret: fetchClientSecret,
          appearance: {
            variables: {
              colorText: "#fff",
              // actionPrimaryColorText: "#000",
              colorSecondaryText: "#9CA3AF",
              badgeDangerColorText: "#000",
              badgeNeutralColorText: "#000",
              badgeSuccessColorText: "#000",
              badgeWarningColorText: "#000",
              buttonPrimaryColorText: "#fff",
              buttonPrimaryColorBackground: "#000",
              offsetBackgroundColor: "#000",
              formAccentColor: "#1c203b",
              actionPrimaryTextDecorationColor: "#fff",
              formHighlightColorBorder: "rgba(255, 255, 255,0)",
              // offsetBackgroundColor: "#1c203b",
              actionPrimaryColorText: "#fff",
              actionSecondaryColorText: "#fff",
              actionSecondaryTextDecorationColor: "#fff",
              formBackgroundColor: "#000",
              colorBorder: "rgba(255, 255, 255,0)",
            },
          },
        })

        setStripeConnectInstance(instance)
        console.log("Stripe Connect initialized successfully")
      } catch (error) {
        console.error("Failed to initialize Stripe Connect:", error)
        setStripeConnectInstance(null)
      } finally {
        setIsInitialized(true)
      }
    }

    // Reset initialization when dependencies change
    setIsInitialized(false)
    initializeStripe()
  }, [user?.uid, stripeUrl?.url, isStripeEmbeddedEnabled])

  // Show loading state while initializing
  if (!isInitialized) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <div>Initializing payment system...</div>
      </div>
    )
  }

  // If Stripe Connect is not needed/available, render without provider
  if (!stripeConnectInstance) {
    console.log("Rendering without Stripe Connect provider")
    return <>{children}</>
  }

  // Render with Stripe Connect provider
  console.log("Rendering with Stripe Connect provider")
  return (
    <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
      {children}
    </ConnectComponentsProvider>
  )
}

function App() {
  const [user, setUser] = useRecoilState(userState)
  const [userInfoWithBuddies, setUserInfoWithBuddies] = useRecoilState(
    userInfoWithBuddiesState,
  )
  const suggestedBuddiesAtomData = useRecoilValue(suggestedBuddiesAtom)
  const getRequestedSuggestedBuddies = useRecoilValue(
    requestedSuggestedBuddiesAtom,
  )
  const requestedSuggestedBuddiesCount = size(getRequestedSuggestedBuddies)

  const stripeUrl = useRecoilValue(stripeUrlState)

  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("savedEventDetails")
    }

    window.addEventListener("beforeunload", handleUnload)

    return () => {
      window.removeEventListener("beforeunload", handleUnload)
    }
  }, [])

  useTrackUser()
  useDevEmailVerification() // 🔥 DEV: Restore fake email verification state

  // 🔥 DEV: Inform developers about fake email verification
  useEffect(() => {
    if (isDev()) {
      console.log(
        "%c🚀 DEV MODE: Fake Email Verification Available!",
        "color: #22c55e; font-weight: bold; font-size: 14px;",
      )
      console.log(
        "%c   • Click 'Verify' button → instantly verified (no real email needed)",
        "color: #10b981; font-size: 12px;",
      )
      console.log(
        "%c   • Verification persists until logout",
        "color: #10b981; font-size: 12px;",
      )
      console.log(
        "%c   • Look for green indicator in bottom-right when active",
        "color: #10b981; font-size: 12px;",
      )
    }
  }, [])

  const tagManagerArgs = {
    gtmId: "GTM-MCFWPZPN",
    uid: user?.uid,
  }

  TagManager.initialize(tagManagerArgs)

  useEffect(() => {
    // If user is logged in, get user info
    if (user) {
      getUserInfo({}).then((res: any) => {
        setUser((oldUser: TWebUser) => {
          // Preserve local country value if backend returns null or empty
          const backendCountry = res?.userInfo?.country || ""
          const localCountry = oldUser?.country || ""

          return {
            ...oldUser,
            ...res?.userInfo,
            // Only update country if backend has a valid value, otherwise keep local value
            country: backendCountry || localCountry || "",
          }
        })
        setUserInfoWithBuddies(res)
      })
    }
  }, [requestedSuggestedBuddiesCount, suggestedBuddiesAtomData, user?.uid])

  useEffect(() => {
    const restrictedRoutes: (typeof ROUTES)[keyof typeof ROUTES][] = [
      ROUTES.PROFILE,
    ]

    if (
      !user?.uid &&
      restrictedRoutes.includes(
        window.location.pathname as (typeof ROUTES)[keyof typeof ROUTES],
      )
    ) {
      // redirect to letshang.info
      window.location.href = "https://letshang.info"
      return
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <DevEmailIndicator />
      <BrowserRouter>
        <DynamicStripeConnectProvider user={user} stripeUrl={stripeUrl}>
          {/* <StripeProvider> */}
          <ToastContainer />
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route
                path={ROUTES.LANDING}
                element={
                  <CheckForRedirection>
                    <LandingPage />
                  </CheckForRedirection>
                }
              />
              <Route path={ROUTES.GROUPS} element={<GroupLandingPage />} />
              <Route path={ROUTES.SIGNUP} element={<SignUpPage />} />
              <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
              <Route path={ROUTES.EXPLORE} element={<Explore />} />
              <Route path={ROUTES.SHARE_EVENTS} element={<RedirectTo />} />
              <Route path={ROUTES.EVENTS} element={<EventLandingPage />} />
              <Route path={ROUTES.CREATE_EVENT} element={<CreateEventPage />} />
              <Route
                path={ROUTES.CREATE_EVENT_FAST_SIGN_UP}
                element={<CreateEventPageV2 />}
              />
              <Route
                path={ROUTES.CREATE_EVENT_V3}
                element={<CreateEventPageV3 />}
              />
              <Route
                path={ROUTES.CREATE_EVENT_V4}
                element={<CreateEventPageV4 />}
              />
              <Route
                path={ROUTES.CREATE_EVENT_V5}
                element={<CreateEventPageV5 />}
              />
              <Route
                path={ROUTES.CREATE_EVENT_V6}
                element={<CreateEventPageV6 />}
              />
              {/* Organization routes */}
              <Route
                path={ROUTES.SHARE_ORGANIZATIONS}
                element={<RedirectTo />}
              />
              <Route
                path={ROUTES.ORGANIZATIONS}
                element={<OrganizationProfile />}
              />
              <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
              <Route
                path={ROUTES.EVENT_SIGNUP}
                element={<ProtectedRouteWrapper element={<EventSignUp />} />}
              />
              <Route path={ROUTES.CHOOSE_TICKET} element={<ChooseTicket />} />
              <Route
                path={ROUTES.FILL_FRIENDS_TICKET_INFO}
                element={<FillFriendsTicketInfo />}
              />
              <Route
                path={ROUTES.FILL_QUESTIONNAIRE}
                element={
                  <ProtectedRouteWrapper element={<FillQuestionnaire />} />
                }
              />
              <Route
                path={ROUTES.FILL_QUESTIONNAIRE_UPDATED}
                element={
                  <ProtectedRouteWrapper
                    element={<FillQuestionnaireUpdated />}
                  />
                }
              />
              <Route
                path={`share${ROUTES.CLAIM_TICKET}`}
                element={<RedirectTo />}
              />
              <Route
                path={ROUTES.CLAIM_TICKET}
                element={<ClaimTicketLandingPage />}
              />
              <Route
                path={ROUTES.STRIPE_CHECKOUT}
                element={<ProtectedRouteWrapper element={<StripeCheckout />} />}
              />
              <Route
                path={ROUTES.STRIPE_SUCCESS}
                element={<ProtectedRouteWrapper element={<StripeSuccess />} />}
              />
              <Route
                path={ROUTES.EVENT_CREATE_SUCCESS}
                element={<EventCreateSuccess />}
              />
              <Route
                path={ROUTES.STRIPE_ONBOARDING_SUCCESS}
                element={<StripeConnectionStatus />}
              />
              <Route path={ROUTES.TICKETS} element={<MyEventTicket />}>
                <Route index element={<TicketCollection />} />
                <Route
                  path={ROUTES.MY_TICKET_ID}
                  element={<IndividualTicket />}
                />
              </Route>

              <Route path={ROUTES.EVENT_ROULLETE} element={<EventRoulette />} />
              <Route path={ROUTES.SPECIAL_EVENT} element={<SpecialEvent />} />
              <Route path="*" element={<Navigate to={ROUTES.LANDING} />} />
              <Route path={ROUTES.PEOPLE} element={<PeoplePage />} />
              <Route
                path={ROUTES.EVENT_TEMPLATES}
                element={<EventTemplates />}
              />
              <Route
                path={ROUTES.COMPLETE_PROFILE}
                element={<CompleteProfile />}
              />
              <Route
                path={ROUTES.PLUGIN_STUDIO_CREATION}
                element={<PluginDevelopmentStudio />}
              />
              <Route
                path={ROUTES.PLUGIN_DETAILS}
                element={<PluginDetailPage />}
              />
              <Route path={ROUTES.EDIT_EVENT} element={<EditEventV5 />} />
              <Route
                path={ROUTES.REVIEW_QUESTIONNAIRE}
                element={<ReviewQuestionnaire />}
              />
              <Route path={ROUTES.GUEST_LIST} element={<GuestList />} />
              <Route
                path={ROUTES.PAYMENT_INSIGHTS}
                element={<PaymentInsights />}
              />
            </Routes>
          </div>
          {/* </StripeProvider> */}
        </DynamicStripeConnectProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default withProviders(App)
